/*  Shared  */
import { Storage } from 'application/Shared/Handlers';
import { Notify, Response } from 'application/Shared/Helpers';
import Core from 'application/Shared/Helpers/Functions';
import { Env, debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';
import * as axiosJWT from 'axios-jwt';
import i18n from 'i18n';
import { matchPath } from 'react-router';

const { t } = i18n;

/*  Constants  */
const baseURL = `${process.env.REACT_APP_BACKEND}/api`;
const SESSION_ERR =
    'We are having problems fetching your session. Please refresh and try again.';
const { hasData, getData, getOne } = Response;

const Session = (() => {
    /**
     * Used as a monitor to enable / disable okta session validation.
     * Enabled on refresh if a session exists or on successful login.
     * By default, this is turned off.
     *
     * It is also used as a guard to ensure that only a single validation request is
     * in progress at any instant.
     */
    const authChannel = new BroadcastChannel('app-auth');

    let isSessionValidationAllowed = false;
    let sessionTenantId = null;
    let sessionJwt = null; // String representation on axios-jwt IAuthTokens

    // If other tabs/browsers refresh jwt, update it in our in-memory version
    authChannel.onmessage = ({ data }) => {
        if (data?.type === 'NEW_TOKEN') {
            sessionJwt = data.token; // Set underlying value (no need to go through axios-jwt)
        }
    };

    // Matches signature from axios-jwt
    const STORAGE = {
        get: async () => sessionJwt,
        remove: async () => {
            if (sessionJwt) {
                sessionJwt = null;
                authChannel.postMessage({ type: 'NEW_TOKEN' });
            }
        },
        set: async (key, value) => {
            if (sessionJwt !== value) {
                sessionJwt = value;
                authChannel.postMessage({ token: value, type: 'NEW_TOKEN' });
            }
        }
    };

    const storeJWT = async (jwt, callbackFn) => {
        try {
            await axiosJWT.setAuthTokens({
                accessToken: jwt,
                refreshToken: 'tokenInCookie'
            });
        } catch (e) {
            debug.e('Failed to store JWT: ', e);
            await axiosJWT.clearAuthTokens();
        } finally {
            callbackFn();
        }
    };

    /**
     * Call the server to refresh accessToken (typically on expiry).
     * @returns {Promise<string>}
     * @throws {Error} If the refresh fails or cannot be made.
     */
    const refreshJWT = async () => {
        const api = axios.create({ baseURL: Env.isLocal() ? '/api' : baseURL });

        debug.i(`GET /authentication/oauth2/refresh`);
        const response = await api.get('/authentication/oauth2/refresh', {
            withCredentials: true
        });

        if (!response?.data?.jwt) {
            throw new Error('Refresh endpoint did not return jwt');
        }

        // @TODO Remove this sometime in 2026 -- clearing out old 'jwt' storage
        localStorage.removeItem('jwt');

        return response.data.jwt;
    };

    const storeAccessToken = (accessToken, ttlMilliseconds, callbackFn) => {
        try {
            Storage.setWithExpiry('accessToken', accessToken, ttlMilliseconds);
        } catch (e) {
            debug.e('Failed to store access token: ', e);
            // localStorage.clear();
        }
        callbackFn(accessToken);
    };

    const getAccessToken = async () =>
        new Promise((resolve) => {
            const token = Storage.getWithExpiry('accessToken');
            if (token !== undefined) {
                resolve(token);
            }
            resolve(null);
        });

    const storeGoogleAppAuthDetails = (
        accessToken,
        refreshToken,
        scopeOk,
        callbackFn
    ) => {
        try {
            Storage.set('gwsAccessToken', accessToken);
            Storage.set('gwsRefreshToken', refreshToken);
            Storage.set('gwsScopeOk', scopeOk);
        } catch (e) {
            debug.e('Failed to store GWS access tokens: ', e);
        }
        callbackFn(accessToken);
    };

    const getGoogleAppAuthDetails = () => {
        const accessToken = localStorage.getItem('gwsAccessToken');
        const refreshToken = localStorage.getItem('gwsRefreshToken');
        const scopeOk = localStorage.getItem('gwsScopeOk');
        if (
            accessToken !== undefined &&
            refreshToken !== undefined &&
            scopeOk !== undefined
        ) {
            return { accessToken, refreshToken, scopeOk };
        }
        return null;
    };

    const storeAdminAccessGrantedByTenant = (
        isAccessGranted,
        azureTenantId,
        callbackFn
    ) => {
        try {
            Storage.set(`adminAccessGranted-${azureTenantId}`, isAccessGranted);
        } catch (e) {
            debug.e('Failed to store admin access grant: ', e);
            // localStorage.clear();
        }
        callbackFn(isAccessGranted);
    };

    const getAdminAccessGrantedByTenant = async (azureTenantId) =>
        new Promise((resolve) => {
            const access = Storage.get(`adminAccessGranted-${azureTenantId}`);
            if (access !== undefined) {
                resolve(access);
            }
            resolve(false);
        });

    const end = async (callbackFn = () => {}) => {
        const token = await STORAGE.get();
        if (token) {
            try {
                const api = axios.create({
                    baseURL: Env.isLocal() ? '/api' : baseURL,
                    headers: { Authorization: token }
                });

                await api.get('/authentication/oauth2/logout', {
                    withCredentials: true
                });
            } catch (err) {
                debug.e('Failure attempt to use logout endpoint', err);
            }
        }
        localStorage.removeItem('accessToken'); // remove Microsoft Entra ID accessToken
        await axiosJWT.clearAuthTokens();
        callbackFn();
        sessionTenantId = null;
    };

    const oktaSessionStarted = (ok) => {
        isSessionValidationAllowed = ok;
    };

    const loadPartnerTenantLinks = async () => {
        try {
            const response = await axios.get('/partnerTenantLink/self', {
                params: {
                    includeClients: true,
                    includeTenant: true
                }
            });
            if (!hasData(response)) {
                return [];
            }

            return getData(response);
        } catch (e) {
            debug.e(e);
            return [];
        }
    };

    const getSessionInfo = async () => {
        const sessionObj = {
            id: null,
            sessionSettings: {},
            tenant: null,
            user: null
        };
        // Get the User
        let user = {};
        try {
            const response = await axios.get('/user/self');
            if (!hasData(response)) {
                debug.e('No User Found', response);
                end();
                Notify.error(SESSION_ERR);
                return null;
            }

            user = getOne(response);
        } catch (e) {
            debug.e(e);
            end();
            Notify.error(SESSION_ERR);
            return null;
        }

        sessionObj.user = user;
        sessionObj.id = user?.id;

        const { permission, disabled } = user;

        if (permission) {
            if (permission.name === 'No UI Access') {
                end(async () => {
                    Core.clearToasts(() => {
                        Notify.error(t('Session.doesNotHavePermission'), 10000);
                    });
                });
                return null;
            }
        }

        if (disabled) {
            end();
            return null;
        }

        // Get the Tenant
        try {
            const response = await axios.get('/tenant/self', {
                params: { includeSettings: true }
            });
            if (!hasData(response)) {
                debug.e('No Tenant Found', response);
                end();
                Notify.error(SESSION_ERR);
                return null;
            }

            const tenant = getOne(response);
            sessionObj.tenant = tenant;
            sessionTenantId = tenant.id;
        } catch (e) {
            debug.e(e);
            end();
            Notify.error(SESSION_ERR);
            return null;
        }

        // Loading anmTenantReservedStatus here since we change sessions often (during signin)
        // and we want to ensure it's accurate the tenant you're viewing.
        const anmTenantReservedStatus = {
            error: null,
            reservedInfo: {}
        };
        try {
            const response = await axios.get('/anm/tenant/reserved');

            if (!Response.hasData(response, { noId: true })) {
                const errMsg = 'No response data';
                debug.e(errMsg, response?.data);
                anmTenantReservedStatus.error = errMsg;
            } else {
                const data = Response.getData(response, { noId: true });
                anmTenantReservedStatus.reservedInfo = data;
            }
        } catch (err) {
            const errMsg = err?.response?.data?.message || err.message;
            // 'Tenant not linked to ANM' is the standard error from the backend. Logging it to the console is not particularly helpful.
            // Let's only log something if the error is not the standard reply.
            if (errMsg !== 'Tenant not linked to ANM') {
                debug.e('Error: ', errMsg);
            }
            anmTenantReservedStatus.error = errMsg;
        }

        sessionObj.sessionSettings.partnerTenantLinks = [];
        if (sessionObj.tenant.partner) {
            sessionObj.sessionSettings.partnerTenantLinks =
                await loadPartnerTenantLinks();
        }

        sessionObj.sessionSettings.anmTenantReservedStatus =
            anmTenantReservedStatus;

        sessionObj.sessionSettings.hubType =
            sessionObj?.tenant?.settings?.hubType || 'None';
        sessionObj.sessionSettings.licensedHub =
            sessionObj.sessionSettings.hubType !== 'None';

        // If the user/tenant does not have a licensedHub AND they have a trialEndDate set, they are a trial customer.
        // Based on this, we can further determine if they are in an active trial or an expired trial.
        const isTrialCustomer =
            !sessionObj.sessionSettings.licensedHub &&
            !!sessionObj?.tenant?.settings?.trialEndDate;

        sessionObj.sessionSettings.inTrial =
            isTrialCustomer &&
            new Date(sessionObj.tenant.settings.trialEndDate).getTime() >
                new Date().getTime();

        sessionObj.sessionSettings.expiredTrial =
            isTrialCustomer &&
            new Date(sessionObj.tenant.settings.trialEndDate).getTime() <=
                new Date().getTime();

        return sessionObj;
    };

    /**
     * Enables Okta session validation if auth type is okta
     * @param {*} isLoggedIn true if user is in session. Otherwise false.
     * @returns
     */
    const enableOktaSessionValidation = async (isLoggedIn) => {
        let authType;
        try {
            authType = await Storage.get('authentication-type');
        } catch (e) {
            if (!isLoggedIn) {
                oktaSessionStarted(false);
                return;
            }
        }
        if (authType === 'google' || authType === 'microsoft') {
            oktaSessionStarted(false);
            return;
        }
        oktaSessionStarted(true);
    };

    /**
     * Validates if an OKTA session exists using the sessions api. Nothing to do for
     * non-okta auth types.
     *
     * @param {*} callback is invoked with result of authentication => Null if auth is
     * unsuccessful, otherwise with session object.
     *
     * @returns
     */
    // @ts-expect-error TODO: Enable eslint when we tackle SAAS-535
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    const validateSessionWithOkta = async (callback) => {
        // Discard session request if another one is in progress.
        if (!isSessionValidationAllowed) {
            return;
        }
        isSessionValidationAllowed = false;
        const envVar = 'REACT_APP_OKTA_DOMAIN';
        const baseUrl = Env.get(envVar);
        if (!baseUrl) {
            debug.e(`Environment variable '${envVar}' is not set.`);
            return;
        }
        // validate okta session synchronously
        const oktaAxios = axios.create({
            baseURL: baseUrl,
            withCredentials: true
        });
        let response;
        try {
            response = await oktaAxios.get('/api/v1/sessions/me');
        } catch (e) {
            debug.e(e);
        }
        // synchronous callback
        callback(response);

        // release hold on monitor to allow another request.
        isSessionValidationAllowed = true;
    };

    // TODO: Enable eslint when we tackle SAAS-535
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    const axiosRouteInterceptors = async (callback, queryClient) => {
        axios.interceptors.request.use(async (cfg) => {
            // Commenting out this validateSessionWithOkta stuff to see if it's the cause of recent login issues.
            // See temp fix for more info ticket: https://auviknetworks.atlassian.net/browse/SAAS-535
            // TODO: If this resolves the logout bug, re-implement this properly. See https://auviknetworks.atlassian.net/browse/SAAS-536
            // if (callback) {
            //     // validate OKTA session asynchronously.
            //     validateSessionWithOkta(callback);
            // }
            const url = cfg.url.replace(baseURL, '');

            const config = {
                ...cfg,
                url: `${baseURL}${url}`
            };

            // Pull the partnerId out of the URL, if it exists.
            const matched = matchPath(
                '/partner/:partnerId/*',
                window.location.pathname
            );

            // checking the sessionTenantId may not really be necessary. We could just always set the
            // requestingTenantId and let the BE decide if the request actually has access to this tenant.
            if (sessionTenantId) {
                // If we have a partnerId from the URL, then it's a partner making the request.
                const partnerId = matched?.params?.partnerId;
                if (partnerId && partnerId !== sessionTenantId) {
                    config.headers.requestingTenantId = partnerId;
                }

                // Is checking the clientId even necessary?
                // in the current state, ONLY partner IDs will ever be using the new partner tenant link
                // so we'll never need to make a request on behalf of a client.
                // all client requests can remain the same, can't they?
                // We already do things like `if (clientID) platformTenantId = clientId` in the frontend
                // OR in the backend, if platformTenantId is in the payload, we consider it a client request
                // and change reqLocals anyway.
                // const clientId = matched?.params?.clientId;
                // if (!partnerId && clientId && clientId !== sessionTenantId) {
                //     config.headers.requestingTenantId = clientId;
                // }
            }

            return config;
        });

        axios.interceptors.response.use((res) => {
            const { config } = res;
            const { method } = config;
            const url = config.url.replace(baseURL, '');
            const queryKey = `/${url.split('/')[1]}`;

            // When we call a post, put or delete, we want the data to update right away. Using react-query, we need to invalidate the queries
            // so that they're refeteched. If we don't invalidate the queries, we will not be able to see the new data until we refresh.
            if (
                method.toUpperCase() === 'POST' ||
                method.toUpperCase() === 'PUT' ||
                method.toUpperCase() === 'DELETE'
            ) {
                queryClient.invalidateQueries({ queryKey: [queryKey] });
                // We have two endpoints that make a change to the GET /notes data. They are /importNote and /completedNote.
                // For these two POST requests, we need to update the incoming notes or else the changes will not be reflected until
                // we refresh the page.
                if (url.toLowerCase().includes('note')) {
                    queryClient.invalidateQueries({ queryKey: ['/note'] });
                }
            }
            return res;
        });
    };

    return {
        axiosRouteInterceptors,
        enableOktaSessionValidation,
        end,
        getAccessToken,
        getAdminAccessGrantedByTenant,
        getGoogleAppAuthDetails,
        getSessionInfo,
        loadPartnerTenantLinks,
        oktaSessionStarted,
        refreshJWT,
        STORAGE,
        storeAccessToken,
        storeAdminAccessGrantedByTenant,
        storeGoogleAppAuthDetails,
        storeJWT
    };
})();

export default Session;
